import React, { useState, useEffect } from 'react';
import { X, Plus, Edit, Trash2, ArrowRight, ArrowLeftRight, Shield, AlertCircle } from 'lucide-react';
import ModalPortal from '../UI/ModalPortal';
import toast from 'react-hot-toast';
import { statuses as statusService } from '../../services/apiService';
import { Loader } from '../UI/LoadingSpinner';
import TransitionForm from './TransitionForm';
import ConfirmDialog from '../UI/ConfirmDialog';

const TransitionsModal = ({ 
  isOpen, 
  onClose, 
  status, 
  onTransitionCreated,
  onTransitionUpdated,
  onTransitionDeleted 
}) => {
  const [transitions, setTransitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTransition, setEditingTransition] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
  const [deleteConfirm, setDeleteConfirm] = useState(null); // transition to delete

  useEffect(() => {
    if (isOpen && status) {
      loadTransitions();
    }
  }, [isOpen, status]);

  useEffect(() => {
    setShowForm(false);
    setEditingTransition(null);
    setFormMode('create');
    setDeleteConfirm(null);
  }, [isOpen, status?.id]);

  const loadTransitions = async () => {
    if (!status) return;
    
    setLoading(true);
    try {
      const response = await statusService.getStatusTransitions(status.id);
      setTransitions(response.data);
    } catch (error) {
      console.error('Error loading transitions:', error);
      toast.error('Error loading transitions: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTransition = () => {
    setFormMode('create');
    setEditingTransition(null);
    setShowForm(true);
  };

  const handleEditTransition = (transition, direction) => {
    console.log('🔍 Edit transition called:', { transition, direction });
    setFormMode('edit');
    setEditingTransition(transition);
    setShowForm(true);
  };

  const handleDeleteTransition = (transition) => {
    setDeleteConfirm(transition);
  };

  const confirmDeleteTransition = async () => {
    try {
      await statusService.deleteStatusTransition(deleteConfirm.id);
      toast.success('Transition deleted');
      loadTransitions();
      if (onTransitionDeleted) {
        onTransitionDeleted(deleteConfirm);
      }
    } catch (error) {
      console.error('Error deleting transition:', error);
      toast.error('Error deleting transition: ' + error.message);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (formMode === 'create') {
        await statusService.createStatusTransition(formData);
        toast.success('Transition created');
        if (onTransitionCreated) {
          onTransitionCreated(formData);
        }
      } else {
        await statusService.updateStatusTransition(editingTransition.id, formData);
        toast.success('Transition updated');
        if (onTransitionUpdated) {
          onTransitionUpdated(editingTransition.id, formData);
        }
      }
      
      setShowForm(false);
      setEditingTransition(null);
      loadTransitions();
    } catch (error) {
      console.error('Error saving transition:', error);
      toast.error('Error saving transition: ' + error.message);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingTransition(null);
  };

  const handleClose = () => {
    if (showForm) {
      handleFormCancel();
      return;
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalPortal onClose={handleClose} ariaLabel="Status transitions">
        <div className="mx-2 flex max-h-[calc(100dvh-1rem)] w-full max-w-4xl min-w-0 flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800 sm:mx-4 sm:max-h-[calc(100dvh-2rem)]">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-700 sm:p-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {showForm ? `${formMode === 'create' ? 'Create' : 'Edit'} Transition` : 'Status Transitions'}: {status?.name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {showForm ? 'Configure the transition between statuses' : 'Manage transitions between statuses'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {!showForm && (
                <button
                  onClick={handleCreateTransition}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Transition
                </button>
              )}
              <button
                onClick={handleClose}
                aria-label={showForm ? 'Cancel transition' : 'Close transitions'}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            {showForm ? (
              <TransitionForm
                mode={formMode}
                initialData={editingTransition}
                fromStatusId={status?.id}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
              />
            ) : (
              <>
                {loading ? (
                  <div className="flex justify-center items-center py-8">
                    <Loader variant="component" size="md" fullHeight={false} />
                  </div>
                ) : transitions.length === 0 ? (
                  <div className="text-center py-8">
                    <ArrowRight className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      No transitions for this status
                    </p>
                    <button
                      onClick={handleCreateTransition}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors mx-auto"
                    >
                      <Plus className="w-4 h-4" />
                      Create First Transition
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Outgoing transitions */}
                    {transitions.from_transitions && transitions.from_transitions.length > 0 && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <ArrowRight className="w-5 h-5 text-blue-600" />
                          Outgoing Transitions
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            ({transitions.from_transitions.length})
                          </span>
                        </h3>
                        <div className="space-y-3">
                          {transitions.from_transitions.map((transition) => (
                            <TransitionCard
                              key={transition.id}
                              transition={transition}
                              direction="from"
                              onEdit={(transition) => handleEditTransition(transition, 'from')}
                              onDelete={handleDeleteTransition}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Incoming transitions */}
                    {transitions.to_transitions && transitions.to_transitions.length > 0 && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <ArrowLeftRight className="w-5 h-5 text-green-600" />
                          Incoming Transitions
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            ({transitions.to_transitions.length})
                          </span>
                        </h3>
                        <div className="space-y-3">
                          {transitions.to_transitions.map((transition) => (
                            <TransitionCard
                              key={transition.id}
                              transition={transition}
                              direction="to"
                              onEdit={(transition) => handleEditTransition(transition, 'to')}
                              onDelete={handleDeleteTransition}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* If no transitions at all */}
                    {(!transitions.from_transitions || transitions.from_transitions.length === 0) &&
                     (!transitions.to_transitions || transitions.to_transitions.length === 0) && (
                      <div className="text-center py-8">
                        <ArrowRight className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                          No transitions for this status
                        </p>
                        <button
                          onClick={handleCreateTransition}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors mx-auto"
                        >
                          <Plus className="w-4 h-4" />
                          Create First Transition
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </ModalPortal>
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDeleteTransition}
        confirmStyle="danger"
        title="Delete Transition"
        message={deleteConfirm
          ? `Are you sure you want to delete the transition "${deleteConfirm.from_status?.name}" → "${deleteConfirm.to_status?.name}"? This action cannot be undone.`
          : ''}
        confirmText="Delete"
      />
    </>
  );
};

const TransitionCard = ({ transition, direction, onEdit, onDelete }) => {
  const isFromDirection = direction === 'from';
  
  // For outgoing transitions: from_status → to_status
  // For incoming transitions: from_status → to_status (display as from_status → to_status)
  const fromStatus = isFromDirection ? transition.from_status : transition.from_status;
  const toStatus = isFromDirection ? transition.to_status : transition.to_status;

  return (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-4">
          {/* From Status */}
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full border border-gray-300"
              style={{ backgroundColor: fromStatus.color || '#6B7280' }}
            ></div>
            <span className="font-medium text-gray-900 dark:text-white">
              {fromStatus.name}
            </span>
          </div>

          {/* Arrow */}
          <div className="flex items-center gap-1">
            {transition.is_bidirectional ? (
              <ArrowLeftRight className="w-4 h-4 text-gray-500" />
            ) : (
              <ArrowRight className="w-4 h-4 text-gray-500" />
            )}
          </div>

          {/* To Status */}
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full border border-gray-300"
              style={{ backgroundColor: toStatus.color || '#6B7280' }}
            ></div>
            <span className="font-medium text-gray-900 dark:text-white">
              {toStatus.name}
            </span>
          </div>

          {/* Properties */}
          <div className="flex items-center gap-2 ml-4">
            {transition.requires_approval && (
              <div className="flex items-center gap-1 text-amber-600" title="Requires approval">
                <Shield className="w-4 h-4" />
                <span className="text-xs">{transition.approval_role}</span>
              </div>
            )}

            {transition.transition_reason_required && (
              <div className="flex items-center gap-1 text-blue-600" title="Requires reason">
                <AlertCircle className="w-4 h-4" />
              </div>
            )}

            {transition.is_bidirectional && (
              <div className="flex items-center gap-1 text-green-600" title="Bidirectional">
                <ArrowLeftRight className="w-4 h-4" />
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(transition)}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(transition)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransitionsModal;
